import { AuthService } from '../services/AuthService';
import { GitHubService } from '../services/GitHubService';
import { withoutGitBusyOverlay } from '../utils/gitBusy';
import { displayNameFromPath, listDatabaseFolders } from '../utils/markdownPaths';
import { sortPagesByName, sortSectionsByTitle } from '../utils/sortPickerChoices';
import { Sidebar } from '../components/Sidebar';
import { SidebarSearch } from '../components/SidebarSearch';
import { FileView } from '../components/FileView';
import type { FileViewDeps } from '../components/fileView/FileViewDeps';
import type { SearchHit } from '../search/contentSearch';
import { getLastUpdatedLabel } from '../utils/buildInfo';
import { escapeAttr } from '../utils/escapeHtml';
import { pageIcon } from '../utils/pageIcon';
import { DB_PREFIX } from './constants';
import type { AppContext } from './appContext';
import { ensureSearchIndex, openSearchHit } from './appSearch';
import * as fileLifecycle from './fileLifecycle';
import * as navHandlers from './navHandlers';
import * as appViews from './appViews';

export interface AppShellResult {
  sidebar: Sidebar;
  sidebarSearch: SidebarSearch;
  fileView: FileView;
}

export function mountAppShell(
  ctx: AppContext,
  github: GitHubService,
  getImageDisplayUrl: (repoPath: string, fullSize?: boolean, bustCache?: boolean) => Promise<string>,
  getAudioPlaybackUrl: (repoPath: string, bustCache?: boolean) => Promise<string>,
  getVideoPlaybackUrl: (
    repoPath: string,
    bustCache?: boolean,
    opts?: import('../utils/videoPlaybackMode').VideoPlaybackOptions
  ) => Promise<string>
): AppShellResult {
  ctx.root.innerHTML = `
      <div class="app-shell">
        <aside id="sidebar" class="sidebar-wrapper"></aside>
        <main id="main" class="main-wrapper">
          <div class="empty-state empty-state--landing">
            <div class="empty-icon" aria-hidden="true"><img src="/codenesium-logo.png" alt="" width="80" height="80" decoding="async" /></div>
            <p class="landing-updated">${escapeAttr(getLastUpdatedLabel())}</p>
          </div>
        </main>
      </div>
    `;

  const sidebarEl = ctx.root.querySelector<HTMLElement>('#sidebar')!;
  const mainEl = ctx.root.querySelector<HTMLElement>('#main')!;

  const fileViewDeps: FileViewDeps = {
    onSave: async file => {
      await fileLifecycle.saveOpenFile(ctx, file);
      ctx.sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
    },
    onRename: async (file, newName) => {
      await fileLifecycle.renameFile(ctx, file, newName, sidebarEl);
    },
    onDelete: async file => {
      await fileLifecycle.deleteFile(ctx, file, sidebarEl);
    },
    onDiscard: file => {
      fileLifecycle.discardOpenFile(ctx, file, mainEl);
    },
    resolveImageUrl: getImageDisplayUrl,
    resolveAudioUrl: getAudioPlaybackUrl,
    resolveVideoUrl: getVideoPlaybackUrl,
    onBeforeDocumentRender: () => {
      ctx.revokeDashboardBlob();
      ctx.revokeImageObjectUrls();
    },
    listOtherPages: currentPath =>
      sortPagesByName(
        ctx.allPaths
          .filter(p => p !== currentPath && p.endsWith('.md') && p.startsWith(DB_PREFIX))
          .map(p => ({ path: p, name: displayNameFromPath(p) }))
      ),
    onMoveItemToNew: async (file, sectionIndex, itemIndex, pageName) => {
      await navHandlers.moveItemToNewPage(ctx, file, sectionIndex, itemIndex, pageName, sidebarEl, mainEl);
    },
    listPageSections: async path => {
      let target = ctx.fileRegistry.files.find(f => f.path === path);
      if (!target) target = await ctx.fileRegistry.ensureLoaded(path);
      return sortSectionsByTitle(
        target.sections.map((s, sectionIndex) => ({ sectionIndex, title: s.title }))
      );
    },
    onMoveItemToSection: async (file, fromSectionIndex, itemIndex, targetPath, targetSectionIndex) => {
      await navHandlers.moveItemToSection(
        ctx,
        file,
        fromSectionIndex,
        itemIndex,
        targetPath,
        targetSectionIndex,
        sidebarEl,
        mainEl
      );
    },
    onMoveAllItemsToSection: async (file, fromSectionIndex, targetPath, targetSectionIndex) => {
      await navHandlers.moveAllItemsToSection(
        ctx,
        file,
        fromSectionIndex,
        targetPath,
        targetSectionIndex,
        sidebarEl,
        mainEl
      );
    },
    onMoveSectionToNew: async (file, sectionIndex, pageName) => {
      await navHandlers.moveSectionToNewPage(ctx, file, sectionIndex, pageName, sidebarEl, mainEl);
    },
    onMoveSectionToPage: async (file, sectionIndex, targetPath) => {
      await navHandlers.moveSectionToExistingPage(
        ctx,
        file,
        sectionIndex,
        targetPath,
        sidebarEl,
        mainEl
      );
    },
    listFolders: () => listDatabaseFolders(ctx.allPaths),
    onMoveFileToFolder: async (file, parentDir) => {
      await navHandlers.moveFileToFolder(ctx, file, parentDir, sidebarEl, mainEl);
    },
    getPageIcon: path => pageIcon(path, ctx.navOrderFile),
    onSetPageIcon: async (path, icon) => {
      await navHandlers.setPageIcon(ctx, path, icon, sidebarEl, mainEl);
    },
  };
  const fileView = new FileView(fileViewDeps);

  const sidebar = new Sidebar(
    async file => {
      const existing = ctx.fileRegistry.files.find(f => f.path === file.path);
      if (existing) {
        ctx.markdown.refreshFileFromProse(existing);
        fileView.render(mainEl, existing);
        return;
      }
      if (file.sections.length !== 0 || file.sha) {
        fileView.render(mainEl, file);
        return;
      }
      mainEl.innerHTML = `<div class="empty-state"><div class="empty-icon" aria-hidden="true"><span class="app-spinner"></span></div><p>Loading…</p></div>`;
      try {
        const { content, sha } = await withoutGitBusyOverlay(() => github.readFile(file.path));
        ctx.fileRegistry.originalContents.set(file.path, content);
        const loaded = ctx.markdown.parse(file.path, content, sha);
        ctx.fileRegistry.files.push(loaded);
        sidebar.setActivePath(file.path);
        sidebar.updateNav(sidebarEl, ctx.allPaths, ctx.fileRegistry.files, ctx.navOrderFile);
        fileView.render(mainEl, loaded);
      } catch (err) {
        mainEl.innerHTML = `<div class="error-state"><p>⚠ Could not load file: ${(err as Error).message}</p></div>`;
      }
    },
    dir => {
      void fileLifecycle.createNewMarkdownInDirectory(ctx, dir, sidebarEl, mainEl);
    },
    dir => {
      void fileLifecycle.createNewFolderInDirectory(ctx, dir, sidebarEl, mainEl);
    },
    dir => {
      void fileLifecycle.importMarkdownInDirectory(ctx, dir, sidebarEl, mainEl);
    },
    (next, fileRelocations) => {
      void navHandlers.applyNavOrderFromSidebar(ctx, next, sidebarEl, mainEl, fileRelocations);
    },
    dir => {
      void navHandlers.renameFolder(ctx, dir, sidebarEl, mainEl);
    },
    () => {
      window.location.reload();
    },
    () => {
      void appViews.showWeather(ctx, sidebarEl, mainEl);
    },
    () => {
      void appViews.showNews(ctx, sidebarEl, mainEl);
    },
    () => {
      void appViews.showDailyUpdate(ctx, sidebarEl, mainEl);
    },
    () => {
      void appViews.showEarTraining(ctx, sidebarEl, mainEl);
    },
    () => {
      void appViews.showChess(ctx, sidebarEl, mainEl);
    },
    () => {
      void appViews.showEncryptLinkTool(ctx, sidebarEl, mainEl);
    },
    () => {
      void appViews.showConfig(ctx, sidebarEl, mainEl);
    },
    () => {
      void appViews.showCursorAgent(ctx, sidebarEl, mainEl);
    }
  );

  sidebar.render(sidebarEl, () => {
    AuthService.clear();
    window.location.reload();
  });

  const sidebarSearch = new SidebarSearch({
    ensureIndex: () => ensureSearchIndex(ctx),
    onSelectHit: (hit: SearchHit) => openSearchHit(ctx, hit, sidebarEl, mainEl),
  });
  const sidebarInner = sidebarEl.querySelector<HTMLElement>('#sidebar-inner');
  if (sidebarInner) sidebarSearch.mount(sidebarInner);

  ctx.sidebar = sidebar;
  ctx.fileView = fileView;

  return { sidebar, sidebarSearch, fileView };
}
